import { useQuery } from '@apollo/client';
import { useState, useEffect, useCallback } from 'react';
import {
    GET_PENDING_CLOSE_ORDERS,
    GetPendingCloseOrdersResponse,
    OrderCloseFiltersInput,
    PageRequestInput
} from '../graphql/queries/getPendingCloseOrders';
import { OrderType } from '../../../graphql/types/OrderType';

export interface OrderCloseQueryResult {
    parcelData: OrderType[];
    loading: boolean;
    refetch: (variables?: any) => Promise<any>;
    totalElements: number;
    buildQueryVariables: () => any;
}

export const useOrderCloseQuery = (
    filters: OrderCloseFiltersInput,
    currentPage: number,
    pageSize: number
): OrderCloseQueryResult => {
    const [parcelData, setParcelData] = useState<OrderType[]>([]);
    const [loading, setLoading] = useState(true);
    const [total, setTotal] = useState(0);

    const buildQueryVariables = useCallback(() => {
        const pageRequest: PageRequestInput = {
            page: currentPage,
            size: pageSize
        };

        // 时间范围转换函数：将 [string | null, string | null] | null 转换为 {start: string, end: string} | undefined
        const convertTimeRange = (range: [string | null, string | null] | null | undefined): { start: string; end: string } | undefined => {
            if (!range || range.length !== 2 || !range[0] || !range[1]) {
                return undefined;
            }
            return { start: range[0], end: range[1] };
        };

        // 只保留后端支持的字段
        // 使用 any 类型来构建 GraphQL 查询变量，因为 GraphQL 会自动处理类型转换
        const backendSupportedFilters: any = {
            trackingNumbers: filters.trackingNumbers,
            channelOrderNumber: filters.channelOrderNumber,
            clientCodes: filters.clientCodes,
            channelIds: filters.channelIds,
            deliveredDspIds: filters.deliveredDspIds,
            daysFromAscan: filters.daysFromAscan,
            hoursToClose: filters.hoursToClose,
            hasException: filters.hasException,
            exceptionStatuses: filters.exceptionStatuses || undefined,
            hasCloseRecord: filters.hasCloseRecord,
            flowType: filters.flowType,
            overallStatuses: filters.overallStatuses,
            sortingCodeSegment1: filters.sortingCodeSegment1,
            sortingCodeSegment2: filters.sortingCodeSegment2,
            sortingCodeSegment3: filters.sortingCodeSegment3,
            // 时间范围筛选（转换为 GraphQL 格式：{ start: string, end: string }）
            orderTime: convertTimeRange(filters.orderTime),
            receivedAtHub: convertTimeRange(filters.receivedAtHub),
            deliveredAt: convertTimeRange(filters.deliveredAt),
            destHubArrivedAt: convertTimeRange(filters.destHubArrivedAt),
            destWarehouseArrivedAt: convertTimeRange(filters.destWarehouseArrivedAt),
            lastDeliveryScanTime: convertTimeRange(filters.lastDeliveryScanTime),
            deliverAt: convertTimeRange(filters.deliverAt),
            updatedAt: convertTimeRange(filters.updatedAt),
            returnArrivalHubAt: convertTimeRange(filters.returnArrivalHubAt),
            // 异常相关筛选
            exceptionReasonCode: filters.exceptionReasonCode,
            markExceptionAt: convertTimeRange(filters.markExceptionAt),
            nextAction: filters.nextAction,
            nextActions: filters.nextActions,
            isComplaint: filters.isComplaint,
            podReviewStatus: filters.podReviewStatus,
            // 其他筛选
            // receiverZipcodeInput 需要转换为数组格式
            recipientZipcode: filters.receiverZipcodeInput 
                ? filters.receiverZipcodeInput.split(/[,\n\r\s]+/).map(s => s.trim()).filter(s => s.length > 0)
                : filters.receiverZipcode,
            // pickupHubId: 单选时使用 Long，多选时使用 List<Long>
            pickupHubId: filters.pickupHubId?.length === 1 ? parseInt(filters.pickupHubId[0], 10) : undefined,
            pickupHubIds: filters.pickupHubId && filters.pickupHubId.length > 0 ? filters.pickupHubId.map(v => parseInt(v, 10)) : undefined,
            cooperationWarehouseId: filters.cooperationWarehouseId,
            // 关单相关筛选
            closeTime: convertTimeRange(filters.closeTime),
            closeOperatorAccountId: filters.closeOperatorAccountId
        };

        // 清理空值
        const cleanFilters: any = {};
        Object.keys(backendSupportedFilters).forEach(key => {
            const value = backendSupportedFilters[key];
            // 跳过 undefined 和 null
            if (value === undefined || value === null) {
                return;
            }
            // 跳过空数组
            if (Array.isArray(value) && value.length === 0) {
                return;
            }
            // 对于 daysFromAscan 和 hoursToClose，检查是否为空对象（min 和 max 都是 null/undefined）
            if (key === 'daysFromAscan' || key === 'hoursToClose') {
                // 检查是否是对象类型
                if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
                    // 如果 min 和 max 都是 null/undefined，跳过这个字段
                    if ((value.min == null || value.min === undefined) && 
                        (value.max == null || value.max === undefined)) {
                        return; // 跳过空的范围对象
                    }
                }
            }
            cleanFilters[key] = value;
        });

        return {
            filters: Object.keys(cleanFilters).length > 0 ? cleanFilters : undefined,
            pageRequest
        };
    }, [filters, currentPage, pageSize]);

    const queryVariables = buildQueryVariables();

    const { data, loading: queryLoading, refetch: queryRefetch } = useQuery<GetPendingCloseOrdersResponse>(
        GET_PENDING_CLOSE_ORDERS,
        {
            variables: queryVariables,
            fetchPolicy: 'cache-and-network',
            notifyOnNetworkStatusChange: true
        }
    );

    useEffect(() => {
        if (data?.getPendingCloseOrders) {
            setParcelData(data.getPendingCloseOrders.content);
            setTotal(data.getPendingCloseOrders.totalElements);
            setLoading(false);
        } else if (queryLoading) {
            setLoading(true);
        }
    }, [data, queryLoading]);

    const refetch = async (variables?: any) => {
        const result = await queryRefetch(variables || buildQueryVariables());
        if (result.data?.getPendingCloseOrders) {
            setParcelData(result.data.getPendingCloseOrders.content);
            setTotal(result.data.getPendingCloseOrders.totalElements);
        }
        return result;
    };

    return {
        parcelData,
        loading,
        refetch,
        totalElements: total,
        buildQueryVariables
    };
};
